import { HubConnectionState, type HubConnection } from "@microsoft/signalr";
import "./friends.css";

interface Person { id: string; displayName: string; avatarUrl?: string }
interface Invitation { id: string; fromName: string; roomCode: string; expiresAt: string }
interface FriendsState {
  book: { code: string; appearOffline: boolean; friends: Person[]; incoming: Person[]; outgoing: Person[]; recent: Person[]; blocked: Person[] };
  presence: Record<string, string>; invitations: Invitation[]; canInvite: boolean; watchableFriends?: string[];
}
interface Result { ok: boolean; error?: string; code?: string; resumeToken?: string }

export function mountFriends(connection: HubConnection, options: {
  signedIn: () => boolean; playing: () => boolean; playerId: () => string;
  joined: (result: Result) => void; serverOrigin: string;
}): { refresh: () => Promise<void> } {
  const escaped = (s: string) => s.replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
  const friendCode = new URLSearchParams(location.search).get("friend");
  if (friendCode && /^[a-f\d]{10}$/i.test(friendCode)) sessionStorage.setItem("pending-friend", friendCode);
  const panel = document.createElement("dialog");
  panel.className = "friends-panel";
  panel.setAttribute("aria-labelledby", "friends-title");
  panel.innerHTML = `<header><div><small>YOUR WAR COUNCIL</small><h2 id="friends-title">Friends & allies</h2></div><button type="button" data-close aria-label="Close friends">×</button></header>
    <div class="friends-body"><div class="friends-guest" hidden><p>Sign in with your existing account to add friends and invite them to your lobby.</p>
    <a class="provider-button" href="${escaped(options.serverOrigin)}/auth/steam/start">Continue with Steam</a><a class="provider-button" data-discord href="${escaped(options.serverOrigin)}/auth/discord/start">Continue with Discord</a></div>
    <div class="friends-account" hidden><div class="friend-code"><div><small>YOUR FRIEND CODE</small><strong data-code>…</strong></div><button data-copy-code>Copy code</button><button data-copy-link>Copy friend link</button></div>
    <form class="friend-add"><label for="friend-code-input">Add a commander</label><div><input id="friend-code-input" maxlength="250" placeholder="Friend code or link" autocomplete="off" required><button type="submit">Send request</button></div></form>
    <section class="friend-invitations" aria-label="Game invitations"></section>
    <p class="friend-muted" data-invite-hint hidden></p>
    <nav class="friends-tabs" aria-label="Friends lists"><button data-tab="friends">Friends</button><button data-tab="requests">Requests</button><button data-tab="recent">Recent players</button><button data-tab="blocked">Blocked</button></nav>
    <div class="friends-list"></div></div><p class="friends-message" role="status" aria-live="polite"></p></div>`;
  document.body.append(panel);
  const notice = document.createElement("aside");
  notice.className = "friend-notice";
  notice.hidden = true;
  notice.setAttribute("aria-live", "polite");
  document.body.append(notice);
  const find = <T extends HTMLElement>(selector: string) => panel.querySelector<T>(selector)!;
  const message = (text: string) => { find(".friends-message").textContent = text; };
  let state: FriendsState | undefined;
  let tab = "friends";
  let fetching = false;
  let pending = false;
  let pendingLinkOpened = false;
  let dismissed = "";
  let listHtml = "";
  const actionButton = (action: string, id: string, text: string, disabled = false) =>
    `<button type="button" data-action="${action}" data-id="${escaped(id)}" ${disabled ? "disabled" : ""}>${text}</button>`;

  function render(): void {
    find(".friends-guest").hidden = options.signedIn();
    find(".friends-account").hidden = !options.signedIn() || !state;
    const discord = document.querySelector<HTMLAnchorElement>("#discord-signin");
    find("[data-discord]").hidden = !discord || discord.classList.contains("hidden");
    if (!state || !options.signedIn()) return;
    const { book } = state;
    const inviteHint = find("[data-invite-hint]");
    inviteHint.hidden = book.friends.length === 0 || (state.canInvite && !book.appearOffline);
    inviteHint.textContent = book.appearOffline ? "You appear offline. Switch it off on the main menu to send invitations." : "Open a lobby with a free player slot to invite friends.";
    find("[data-code]").textContent = book.code;
    document.querySelectorAll<HTMLInputElement>("[data-appear-offline]").forEach(input => {
      input.checked = book.appearOffline;
      input.disabled = false;
    });
    const total = book.incoming.length + state.invitations.length;
    document.querySelectorAll<HTMLButtonElement>("[data-open-friends]").forEach(button => {
      button.textContent = `${button.dataset.recent !== undefined ? "Recent players" : "Friends"}${total ? ` (${total})` : ""}`;
    });
    panel.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach(button => {
      button.setAttribute("aria-current", String(button.dataset.tab === tab));
      if (button.dataset.tab === "requests") button.textContent = `Requests${book.incoming.length ? ` (${book.incoming.length})` : ""}`;
    });
    find(".friend-invitations").innerHTML = state.invitations.map(invite => `<article><div><strong>${escaped(invite.fromName)}</strong><span>Invited you to play · expires in ${Math.max(1, Math.ceil((Date.parse(invite.expiresAt) - Date.now()) / 60000))} min</span></div>${actionButton("join", invite.id, "Join", options.playing())}${actionButton("dismiss", invite.id, "Decline")}</article>`).join("");
    const row = (person: Person, kind: string) => {
      const friend = book.friends.some(p => p.id === person.id);
      const outgoing = book.outgoing.some(p => p.id === person.id);
      const incoming = book.incoming.some(p => p.id === person.id);
      const presence = friend ? state!.presence[person.id] ?? "offline" : kind === "outgoing" ? "Request sent" : "";
      const actions = kind === "incoming" ? actionButton("accept", person.id, "Accept") + actionButton("decline", person.id, "Decline") :
        kind === "outgoing" ? actionButton("cancel", person.id, "Cancel request") :
        kind === "blocked" ? actionButton("unblock", person.id, "Unblock") :
        friend ? (state!.watchableFriends?.includes(person.id) ? actionButton("spectate", person.id, "Spectate", options.playing()) : actionButton("invite", person.id, "Invite", !state!.canInvite || book.appearOffline || presence !== "online")) :
        incoming ? actionButton("accept", person.id, "Accept request") :
        outgoing ? `<span class="friend-muted">Request sent</span>` : actionButton("recent", person.id, "Add friend");
      return `<article class="friend-row"><div class="friend-monogram" aria-hidden="true">${escaped(person.displayName.slice(0, 1).toUpperCase())}</div><div class="friend-identity"><strong>${escaped(person.displayName)}</strong><span class="${presence === "online" ? "friend-online" : ""}">${escaped(presence)}</span></div><div class="friend-actions">${actions}</div>${kind !== "blocked" ? `<details><summary aria-label="More actions for ${escaped(person.displayName)}">•••</summary><div>${friend ? actionButton("remove", person.id, "Remove friend") : ""}${actionButton("block", person.id, "Block")}</div></details>` : ""}</article>`;
    };
    let html = "";
    if (tab === "requests") html = book.incoming.map(p => row(p, "incoming")).join("") + book.outgoing.map(p => row(p, "outgoing")).join("");
    else {
      const people = tab === "recent" ? book.recent : tab === "blocked" ? book.blocked : [...book.friends].sort((a, b) =>
        Number(state!.presence[a.id] === "offline") - Number(state!.presence[b.id] === "offline") || a.displayName.localeCompare(b.displayName));
      html = people.map(p => row(p, tab)).join("");
    }
    const empty = tab === "friends" ? "Your council is waiting. Share your friend code or add a commander above." : tab === "recent" ? "Signed-in commanders you finish matches with will appear here." : tab === "blocked" ? "No blocked commanders. Blocking removes friendship and prevents requests and invitations." : "No friend requests waiting.";
    html ||= `<p class="friends-empty">${empty}</p>`;
    if (html !== listHtml) { find(".friends-list").innerHTML = html; listHtml = html; }
    const first = state.invitations[0];
    notice.hidden = !first || options.playing() || panel.open || dismissed === first.id;
    if (first) notice.innerHTML = `<strong>${escaped(first.fromName)} invited you to play</strong><button data-review>View invitation</button><button data-hide aria-label="Dismiss invitation notification">×</button>`;
  }

  async function refresh(): Promise<void> {
    if (fetching || pending || !options.signedIn() || connection.state !== HubConnectionState.Connected) return;
    fetching = true;
    try {
      state = await connection.invoke<FriendsState>("GetFriends");
      render();
      if (!pendingLinkOpened && sessionStorage.getItem("pending-friend") && !options.playing()) {
        pendingLinkOpened = true; open();
      }
    } catch { if (panel.open) message("Friends are temporarily unavailable. Reconnecting…"); }
    finally { fetching = false; }
  }

  function open(): void {
    if (!panel.open) panel.showModal();
    const code = sessionStorage.getItem("pending-friend");
    if (code) find<HTMLInputElement>("#friend-code-input").value = code;
    render();
    if (options.signedIn() && !state) message("Connecting to your council…");
    void refresh();
    notice.hidden = true;
  }

  async function run(method: string, args: unknown[], success: string): Promise<void> {
    if (pending) return;
    pending = true;
    panel.setAttribute("aria-busy", "true");
    message("Working…");
    try {
      const result = await connection.invoke<Result>(method, ...args);
      message(result.ok ? success : result.error ?? "That action could not be completed.");
      if (result.ok && method === "AnswerFriendInvite" && args[2]) {
        options.joined(result); panel.close();
      }
      if (result.ok && method === "SpectateFriend") panel.close();
      if (result.ok && method === "FriendAction" && args[0] === "request") {
        sessionStorage.removeItem("pending-friend"); find<HTMLInputElement>("#friend-code-input").value = "";
      }
    } catch { message("Could not reach the server. Please try again."); }
    finally { pending = false; panel.removeAttribute("aria-busy"); await refresh(); }
  }

  document.addEventListener("click", event => {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-open-friends]");
    if (button) { tab = button.dataset.recent !== undefined ? "recent" : "friends"; open(); }
  });
  panel.addEventListener("keydown", event => event.stopPropagation());
  panel.addEventListener("close", () => { dismissed = state?.invitations[0]?.id ?? ""; });
  panel.addEventListener("click", async event => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button");
    if (!button || pending) return;
    if (button.hasAttribute("data-close")) { panel.close(); return; }
    if (button.dataset.tab) { tab = button.dataset.tab; render(); return; }
    if (button.hasAttribute("data-copy-code") || button.hasAttribute("data-copy-link")) {
      const text = button.hasAttribute("data-copy-code") ? state!.book.code : `${location.origin}/?friend=${state!.book.code}`;
      try { await navigator.clipboard.writeText(text); message("Copied. Share it with your friend."); }
      catch { message(`Copy this: ${text}`); }
      return;
    }
    const { action, id } = button.dataset;
    if (!action || !id) return;
    if (action === "spectate") await run("SpectateFriend", [id], "Watching your friend's match.");
    else if (action === "invite") await run("InviteFriend", [id], "Invitation sent. Your friend has two minutes to join.");
    else if (action === "join" || action === "dismiss") await run("AnswerFriendInvite", [id, options.playerId(), action === "join"], "Invitation declined.");
    else await run("FriendAction", [action, id], ({ accept: "Friend added.", decline: "Request declined.", cancel: "Request cancelled.", remove: "Friend removed.", block: "Commander blocked.", unblock: "Commander unblocked. Friendship has not been restored.", recent: "Friend request sent." } as Record<string, string>)[action] ?? "Updated.");
  });
  find<HTMLFormElement>(".friend-add").addEventListener("submit", event => {
    event.preventDefault();
    let code = find<HTMLInputElement>("#friend-code-input").value.trim();
    try { code = new URL(code).searchParams.get("friend") ?? code; } catch { /* Plain friend code. */ }
    if (!/^[a-f\d]{10}$/i.test(code)) { message("Enter a 10-character friend code or a friend link."); return; }
    void run("FriendAction", ["request", code], "Friend request sent. They can accept it in Requests.");
  });
  document.addEventListener("change", async event => {
    const input = event.target as HTMLInputElement;
    if (!input.matches("[data-appear-offline]")) return;
    input.disabled = true;
    const status = document.querySelector<HTMLElement>("[data-presence-status]");
    if (status) status.textContent = "Saving…";
    try {
      const result = await connection.invoke<Result>("FriendAction", "offline", String(input.checked));
      if (!result.ok) throw new Error(result.error ?? "Could not save presence.");
      if (status) status.textContent = input.checked ? "You appear offline to friends." : "Friends can see your status.";
    } catch {
      input.checked = state?.book.appearOffline ?? false;
      if (status) status.textContent = "Could not save. Please try again.";
    } finally { input.disabled = false; await refresh(); }
  });
  notice.addEventListener("click", event => {
    if ((event.target as HTMLElement).closest("[data-review]")) open();
    else { dismissed = state?.invitations[0]?.id ?? ""; notice.hidden = true; }
  });
  connection.on("FriendsChanged", () => { void refresh(); });
  document.addEventListener("commander-account-changed", () => { render(); void refresh(); });
  connection.on("Lobby", () => { void refresh(); });
  connection.on("Snapshot", () => { if (options.playing()) { notice.hidden = true; if (panel.open) panel.close(); } });
  connection.onreconnected(() => { void refresh(); });
  connection.onreconnecting(() => { notice.hidden = true; message("Reconnecting…"); });
  connection.onclose(() => { notice.hidden = true; message("Connection lost. Reconnect to use friends."); });
  // Also catches completed matches and account changes without a second presence protocol.
  window.setInterval(() => { void refresh(); }, 12000);
  if (sessionStorage.getItem("pending-friend")) queueMicrotask(() => { if (!options.playing()) open(); });
  return { refresh };
}
